/**
 * 文件路径: D:/XXY/MeiShan_Project/frontend/shared/api/hardware.ts
 * 硬件模块 API：餐柜池与策略模板
 */

import { httpClient } from './http'

export type CabinetOperationType = 'DEPOSIT' | 'DEPOSIT_COLD' | 'PICKUP'

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value)
}

function toRecord(value: unknown): Record<string, unknown> {
  return isRecord(value) ? value : {}
}

export interface CabinetPool {
  id: string
  enterprise_id?: string
  enterprise_name?: string
  name: string
  description?: string
  total_slots?: number
  available_slots?: number
  city?: string
  workplace_ids?: string[]
  workplaces_detail?: Array<{ id: string; name: string }>
  pool_config?: Record<string, unknown>
  workplace_scope?: string[] // ⚠️ 兼容字段：历史 JSON 范围
  created_at?: string
  updated_at?: string
}

export interface CabinetPolicyTemplate {
  id: string
  name: string
  description?: string
  allocation_strategy?: 'sequential' | 'batch-intelligent' | 'contiguous'
  reserve_policy?: { mode: 'per_30_qty' | 'custom'; custom_qty?: number }
  preheat?: { enabled: boolean; lead_minutes?: number }
  overflow_nearby?: boolean
  created_at?: string
  updated_at?: string
}

export interface SmartCabinetSummary {
  id: string
  code: string
  name: string
  cabinetLabel?: string
  status?: string
  province?: string
  city?: string
  district?: string
  locationDesc?: string
  location?: string
  streetAddress?: string
  address?: string
  totalSlots?: number
  availableSlots?: number
}

export interface HardwareCommandItem {
  id: string
  job_id: string
  cabinet_id: string
  cabinet_code: string
  command: string
  args: Record<string, unknown>
  status: 'PENDING' | 'SENT' | 'ACK' | 'FAILED' | string
  issued_at: string | null
  executed_at: string | null
  issued_by: string
}

export interface HardwareCommandListData {
  total: number
  page: number
  page_size: number
  results: HardwareCommandItem[]
}

export interface HardwareCommandDetail extends HardwareCommandItem {
  response_payload?: unknown | null
}

export interface HardwareEventItem {
  id: string
  cabinet_id: string
  cabinet_code: string
  cabinet_name: string
  event_type: string
  payload: Record<string, unknown>
  reported_at?: string | null
  received_at?: string | null
}

export interface HardwareEventListData {
  total: number
  page: number
  page_size: number
  results: HardwareEventItem[]
}

export interface CabinetOverflowZone {
  id: string
  name: string
  description?: string
  nearby_radius_m: number
  enable_nearby?: boolean
  is_active: boolean
  workplace_count?: number
  cabinet_count?: number
  created_at?: string
  updated_at?: string
}

class HardwareApiService {
  // ⚠️ 后端已在 /api/v1/ 顶层挂载 hardware.api.urls（无额外前缀）
  // baseURL 由 httpClient 统一配置为 /api/v1/，此处只拼接相对路径
  private readonly base = ''

  async getCabinetPools(params?: Record<string, unknown>): Promise<CabinetPool[]> {
    try {
      const payload = await httpClient.get<{
        results?: CabinetPool[]
        message?: string
        status?: string
      }>(`${this.base}cabinet-pools/`, { params })
      return Array.isArray(payload?.results) ? payload.results : []
    } catch (e) {
      // Mock 回退：当后端未就绪或 404 时返回占位数据
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return [
          { id: 'pool_a', name: '默认餐柜池A', description: '示例池', total_slots: 100, available_slots: 80 },
          { id: 'pool_b', name: '默认餐柜池B', description: '示例池', total_slots: 60, available_slots: 55 }
        ]
      }
      throw e
    }
  }

  async createCabinetPool(data: Partial<CabinetPool>): Promise<CabinetPool> {
    // httpClient 会自动将 `{data: ...}` 解包为内部 `data`
    const payload = await httpClient.post<unknown>(`${this.base}cabinet-pools/`, data)
    const p = toRecord(payload)
    const id = String(p.id ?? '')
    return { id, name: String(data.name ?? ''), description: data.description ?? '' }
  }

  async updateCabinetPool(id: string, data: Partial<CabinetPool>): Promise<CabinetPool> {
    // httpClient 会自动将 `{data: ...}` 解包为内部 `data`
    const payload = await httpClient.patch<unknown>(`${this.base}cabinet-pools/${id}/`, data)
    const p = toRecord(payload)
    const rid = String(p.id ?? id)
    return { id: rid, name: String(data.name ?? ''), description: data.description ?? '' }
  }

  async deleteCabinetPool(id: string): Promise<void> {
    await httpClient.delete(`${this.base}cabinet-pools/${id}/`)
  }

  async getCabinetPoolMembers(poolId: string): Promise<Array<{ cabinet_id: string; cabinet_code: string; cabinet_name: string; cabinet_type: string; cabinet_label: string }>> {
    const payload = await httpClient.get<{
      results?: Array<{ cabinet_id: unknown; cabinet_code: string; cabinet_name: string; cabinet_type?: string; cabinet_label?: string }>
      message?: string
      status?: string
    }>(`${this.base}cabinet-pools/${poolId}/members/`)
    const results = Array.isArray(payload?.results) ? payload.results : []
    return results.map(r => ({
      cabinet_id: String(r.cabinet_id ?? ''),
      cabinet_code: String(r.cabinet_code ?? ''),
      cabinet_name: String(r.cabinet_name ?? ''),
      cabinet_type: String(r.cabinet_type ?? 'SMART'),
      cabinet_label: String(r.cabinet_label ?? ''),
    }))
  }

  async setCabinetPoolMembers(poolId: string, cabinetIds: string[]): Promise<{
    saved_count: number
    requested_count: number
    created_count: number
    removed_count: number
    claimed_count: number
    skipped_ids: string[]
  }> {
    const payload = await httpClient.post<{
      saved_count?: number
      requested_count?: number
      created_count?: number
      removed_count?: number
      claimed_count?: number
      skipped_ids?: string[]
      message?: string
      status?: string
    }>(`${this.base}cabinet-pools/${poolId}/members/`, { cabinet_ids: cabinetIds })
    return {
      saved_count: Number(payload?.saved_count ?? 0),
      requested_count: Number(payload?.requested_count ?? 0),
      created_count: Number(payload?.created_count ?? 0),
      removed_count: Number(payload?.removed_count ?? 0),
      claimed_count: Number(payload?.claimed_count ?? 0),
      skipped_ids: Array.isArray(payload?.skipped_ids) ? payload.skipped_ids.map(String) : [],
    }
  }

  async getSmartCabinets(params?: Record<string, unknown>): Promise<SmartCabinetSummary[]> {
    const payload = await httpClient.get<{
      total?: number
      results?: Array<{
        id: unknown
        code: string
        name: string
        cabinet_label?: string
        status?: string
        province?: string
        city?: string
        district?: string
        location_desc?: string
        location?: string
        street_address?: string
        address?: string
        total_slots?: number
        available_slots?: number
      }>
      meta?: unknown
    }>(`${this.base}smart-cabinets/`, { params })
    const results = Array.isArray(payload?.results) ? payload.results : []
    return results.map((r) => {
      const rr = toRecord(r as unknown)
      const cabinetLabel = String((rr.cabinet_label ?? rr.cabinetLabel ?? '') as unknown)
      const streetAddressRaw = rr.street_address ?? rr.streetAddress
      const streetAddress = streetAddressRaw !== undefined ? String(streetAddressRaw) : undefined
      const addressRaw = rr.address
      const address = addressRaw !== undefined ? String(addressRaw) : undefined
      return {
        id: String(r.id ?? ''),
        code: String(r.code ?? ''),
        name: String(r.name ?? ''),
        cabinetLabel,
        status: r.status ?? undefined,
        province: r.province ?? undefined,
        city: r.city ?? undefined,
        district: r.district ?? undefined,
        locationDesc: r.location_desc ?? undefined,
        location: r.location ?? undefined,
        streetAddress,
        address,
        totalSlots: typeof r.total_slots === 'number' ? r.total_slots : undefined,
        availableSlots: typeof r.available_slots === 'number' ? r.available_slots : undefined,
      }
    })
  }

  // ========== operator-admin: cabinets CRUD ==========

  async getOperatorCabinets(params?: Record<string, unknown>): Promise<Array<Record<string, unknown>>> {
    // 后端返回：{data: {total, results[]}, ...}；httpClient 会解包为 {total, results[]}
    const payload = await httpClient.get<{ total?: number; results?: unknown[] }>(`${this.base}cabinets/`, { params })
    const results = Array.isArray(payload?.results) ? payload.results : []
    return results.map((x) => toRecord(x))
  }

  async getOperatorCabinetDetail(cabinetId: string): Promise<Record<string, unknown> | null> {
    // 后端返回：{data: {...}, ...}；httpClient 会解包为 {...}
    const payload = await httpClient.get<unknown>(`${this.base}cabinets/${cabinetId}/`)
    return isRecord(payload) ? payload : null
  }

  async createOperatorCabinet(data: Record<string, unknown>): Promise<Record<string, unknown>> {
    // 后端返回：{data: {...}, ...}；httpClient 会解包为 {...}
    const payload = await httpClient.post<unknown>(`${this.base}cabinets/`, data)
    return toRecord(payload)
  }

  async updateOperatorCabinet(cabinetId: string, data: Record<string, unknown>): Promise<Record<string, unknown>> {
    // 后端返回：{data: {...}, ...}；httpClient 会解包为 {...}
    const payload = await httpClient.patch<unknown>(`${this.base}cabinets/${cabinetId}/`, data)
    return toRecord(payload)
  }

  async deleteOperatorCabinet(cabinetId: string): Promise<void> {
    await httpClient.delete(`${this.base}cabinets/${cabinetId}/`)
  }

  /**
   * MQTT 餐柜迁移 Broker 地址（changeNetwork 命令）
   * POST /api/v1/hardware/cabinets/{cabinet_id}/change-broker/
   */
  async changeCabinetBroker(
    cabinetId: string,
    data: { target_broker_ip: string; target_broker_port?: number }
  ): Promise<Record<string, unknown>> {
    const payload = await httpClient.post<unknown>(
      `${this.base}cabinets/${cabinetId}/change-broker/`,
      data
    )
    return toRecord(payload)
  }

  // ========== P2-T5: manual cabinet operations ==========

  /**
   * 手动柜单格口状态变更
   * POST /api/v1/hardware/cabinets/{cabinet_code}/cells/{cell_id}/manual-update/
   */
  async manualCellUpdate(
    cabinetCode: string,
    cellId: string,
    data: { operation: CabinetOperationType; operator_note?: string; order_id?: string }
  ): Promise<Record<string, unknown>> {
    const payload = await httpClient.post<unknown>(
      `${this.base}cabinets/${cabinetCode}/cells/${cellId}/manual-update/`,
      data
    )
    return toRecord(payload)
  }

  /**
   * 手动柜批量格口状态变更
   * POST /api/v1/hardware/cabinets/{cabinet_code}/manual-batch-update/
   */
  async manualBatchUpdate(
    cabinetCode: string,
    data: { cell_ids: string[]; operation: CabinetOperationType; operator_note?: string }
  ): Promise<Record<string, unknown>> {
    const payload = await httpClient.post<unknown>(
      `${this.base}cabinets/${cabinetCode}/manual-batch-update/`,
      data
    )
    return toRecord(payload)
  }

  /**
   * 手动柜清柜（支持三模式）
   * POST /api/v1/hardware/cabinets/{cabinet_code}/manual-clear/
   * clear_mode: FULL_CABINET | OCCUPIED_ONLY | SPECIFIED_CELLS
   */
  async manualClearCabinet(
    cabinetCode: string,
    operatorNote?: string,
    clearMode?: 'FULL_CABINET' | 'OCCUPIED_ONLY' | 'SPECIFIED_CELLS',
    cellIds?: string[]
  ): Promise<Record<string, unknown>> {
    const body: Record<string, unknown> = { operator_note: operatorNote || '' }
    if (clearMode) body.clear_mode = clearMode
    if (cellIds && cellIds.length > 0) body.cell_ids = cellIds
    const payload = await httpClient.post<unknown>(
      `${this.base}cabinets/${cabinetCode}/manual-clear/`,
      body
    )
    return toRecord(payload)
  }

  async getCabinetCells(cabinetId: string, params?: Record<string, unknown>): Promise<Array<Record<string, unknown>>> {
    const payload = await httpClient.get<{ results?: unknown[] }>(`${this.base}cabinets/${cabinetId}/cells/`, { params })
    const results = Array.isArray(payload?.results) ? payload.results : []
    return results.map((x) => toRecord(x))
  }

  async getCabinetLayout(cabinetCodeOrId: string): Promise<unknown | null> {
    // 后端返回：{data: {...}, ...}；httpClient 会解包为 {...}
    const payload = await httpClient.get<unknown>(`${this.base}smart-cabinets/${cabinetCodeOrId}/layout/`)
    return payload ?? null
  }

  async openCabinetDoor(
    cabinetCodeOrId: string,
    slotNumber: string,
    operationType: CabinetOperationType = 'PICKUP',
    orderId?: string
  ): Promise<void> {
    await httpClient.post(`${this.base}smart-cabinets/${cabinetCodeOrId}/open-door/`, {
      slot_number: slotNumber,
      operation_type: operationType,
      order_id: orderId,
    })
  }

  async listOperatorWorkplaces(params?: Record<string, unknown>): Promise<Array<Record<string, unknown>>> {
    // 后端返回：{data: {total, results[]}, ...}；httpClient 会解包为 {total, results[]}
    const payload = await httpClient.get<{ total?: number; results?: unknown[] }>(`${this.base}operator-workplaces/`, { params })
    const results = Array.isArray(payload?.results) ? payload.results : []
    return results.map((x) => toRecord(x))
  }

  async createCabinetCommand(cabinetId: string, command: string, args?: Record<string, unknown>): Promise<{ id: string }> {
    // 输入：cabinetId, command, args?
    // 输出：{ id: command_id }
    // 后端返回：{data: {id}, ...}；httpClient 会解包为 {id}
    const payload = await httpClient.post<{ id?: unknown }>(`${this.base}cabinets/${cabinetId}/commands/`, {
      command,
      args: args || {},
    })
    return { id: String(payload?.id ?? '') }
  }

  async listCabinetCommands(cabinetId: string, params?: Record<string, unknown>): Promise<HardwareCommandListData> {
    // 输入：cabinetId + 可选 query（page/page_size/status/command/job_id）
    // 输出：{total,page,page_size,results[]}
    const payload = await httpClient.get<HardwareCommandListData>(`${this.base}cabinets/${cabinetId}/commands/`, { params })
    return payload
  }

  async getCabinetCommandDetail(cabinetId: string, commandId: string): Promise<HardwareCommandDetail> {
    // 输入：cabinetId + commandId
    // 输出：详情对象（含 response_payload）
    const payload = await httpClient.get<HardwareCommandDetail>(`${this.base}cabinets/${cabinetId}/commands/${commandId}/`)
    return payload
  }

  async listCabinetEvents(params?: Record<string, unknown>): Promise<HardwareEventListData> {
    // 输入：cabinet/event_type/start_at/end_at/page/page_size
    // 输出：{total,page,page_size,results[]}
    const payload = await httpClient.get<HardwareEventListData>(`${this.base}cabinet-events/`, { params })
    return payload
  }

  async createCabinetPreheatSchedule(
    cabinetId: string,
    payload: { start_at: string; end_at: string; temp_target: number }
  ): Promise<{ id: string }> {
    // 输入：start_at/end_at ISO-8601 string，temp_target number
    // 输出：{ id: schedule_id }
    const resp = await httpClient.post<{ id?: unknown }>(`${this.base}cabinets/${cabinetId}/preheat/`, payload)
    return { id: String(resp?.id ?? '') }
  }

  async getCabinetOverflowBinding(
    primaryCabinetId: string
  ): Promise<{ primaryCabinetId: string; overflowCabinetId: string; overflowCabinetDetail: Record<string, unknown> | null }> {
    // 后端返回：{data:{primary_cabinet_id, overflow_cabinet_id, overflow_cabinet_detail}, ...}；httpClient 会解包为 data
    const payload = await httpClient.get<{
      primary_cabinet_id?: unknown
      overflow_cabinet_id?: unknown
      overflow_cabinet_detail?: unknown
    }>(`${this.base}cabinets/${primaryCabinetId}/overflow-config/`)
    return {
      primaryCabinetId: String(payload?.primary_cabinet_id ?? primaryCabinetId),
      overflowCabinetId: String(payload?.overflow_cabinet_id ?? ''),
      overflowCabinetDetail: isRecord(payload?.overflow_cabinet_detail) ? payload.overflow_cabinet_detail : null,
    }
  }

  async setCabinetOverflowBinding(primaryCabinetId: string, overflowCabinetId?: string | null): Promise<void> {
    // 允许为空：表示解绑
    await httpClient.post(`${this.base}cabinets/${primaryCabinetId}/overflow-config/`, {
      overflow_cabinet_id: overflowCabinetId ? String(overflowCabinetId) : null,
    })
  }


  async listCabinetOverflowZones(): Promise<CabinetOverflowZone[]> {
    const payload = await httpClient.get<{ results?: CabinetOverflowZone[] }>(`${this.base}cabinet-overflow-zones/`)
    return Array.isArray(payload?.results) ? payload.results : []
  }

  async createCabinetOverflowZone(data: {
    name: string
    description?: string
    nearby_radius_m?: number
    enable_nearby?: boolean
    is_active?: boolean
  }): Promise<{ id: string }> {
    const payload = await httpClient.post<{ id?: unknown }>(`${this.base}cabinet-overflow-zones/`, data)
    return { id: String(payload?.id ?? '') }
  }

  async updateCabinetOverflowZone(
    zoneId: string,
    data: Partial<{ name: string; description: string; nearby_radius_m: number; enable_nearby: boolean; is_active: boolean }>
  ): Promise<void> {
    await httpClient.patch(`${this.base}cabinet-overflow-zones/${zoneId}/`, data)
  }

  async deleteCabinetOverflowZone(zoneId: string): Promise<void> {
    await httpClient.delete(`${this.base}cabinet-overflow-zones/${zoneId}/`)
  }

  async getCabinetOverflowZoneWorkplaces(zoneId: string): Promise<
    Array<{ workplace_id: string; enterprise_id?: string; enterprise_name?: string; workplace_name?: string }>
  > {
    const payload = await httpClient.get<{ results?: Array<Record<string, unknown>> }>(
      `${this.base}cabinet-overflow-zones/${zoneId}/workplaces/`
    )
    const items = Array.isArray(payload?.results) ? payload.results : []
    return items
      .filter((x) => x && String(x.workplace_id || '').trim())
      .map((x) => ({
        workplace_id: String(x.workplace_id || ''),
        enterprise_id: String(x.enterprise_id || ''),
        enterprise_name: String(x.enterprise_name || ''),
        workplace_name: String(x.workplace_name || ''),
      }))
  }

  async setCabinetOverflowZoneWorkplaces(zoneId: string, workplaceIds: string[]): Promise<void> {
    await httpClient.patch(`${this.base}cabinet-overflow-zones/${zoneId}/workplaces/`, { workplace_ids: workplaceIds })
  }

  async getCabinetOverflowZoneCabinets(zoneId: string): Promise<
    Array<{ cabinet_id: string; cabinet_code?: string; cabinet_name?: string; cabinet_label?: string; allow_borrow: boolean }>
  > {
    const payload = await httpClient.get<{ results?: Array<Record<string, unknown>> }>(
      `${this.base}cabinet-overflow-zones/${zoneId}/cabinets/`
    )
    const items = Array.isArray(payload?.results) ? payload.results : []
    return items
      .filter((x) => x && String(x.cabinet_id || '').trim())
      .map((x) => ({
        cabinet_id: String(x.cabinet_id || ''),
        cabinet_code: String(x.cabinet_code || ''),
        cabinet_name: String(x.cabinet_name || ''),
        cabinet_label: String(x.cabinet_label || ''),
        allow_borrow: Boolean(x.allow_borrow === true),
      }))
  }

  async setCabinetOverflowZoneCabinets(
    zoneId: string,
    cabinets: Array<{ cabinet_id: string; allow_borrow: boolean }>
  ): Promise<void> {
    await httpClient.patch(`${this.base}cabinet-overflow-zones/${zoneId}/cabinets/`, { cabinets })
  }

  async getCabinetPolicies(params?: Record<string, unknown>): Promise<CabinetPolicyTemplate[]> {
    try {
      return await httpClient.get<CabinetPolicyTemplate[]>(`${this.base}cabinet-policies/`, { params })
    } catch (e) {
      if (import.meta.env.VITE_USE_MOCK !== 'false') {
        return [
          { id: 'policy_default', name: '通用策略', allocation_strategy: 'sequential', overflow_nearby: true },
        ]
      }
      throw e
    }
  }

  async createCabinetPolicy(data: Partial<CabinetPolicyTemplate>): Promise<CabinetPolicyTemplate> {
    return await httpClient.post<CabinetPolicyTemplate>(`${this.base}cabinet-policies/`, data)
  }

  async updateCabinetPolicy(id: string, data: Partial<CabinetPolicyTemplate>): Promise<CabinetPolicyTemplate> {
    return await httpClient.patch<CabinetPolicyTemplate>(`${this.base}cabinet-policies/${id}/`, data)
  }

  // =====================================================================
  // 职场级批量操作（运营端）
  // =====================================================================

  /**
   * 职场级批量开门（跨多台柜 fan-out）
   * POST /api/v1/hardware/workplace-cabinets/{workplaceId}/batch-open/
   */
  async workplaceBatchOpen(workplaceId: string, params: {
    service_date: string
    meal_segment_code: string
    consumption_type: string
    operation_type: 'PICKUP' | 'DEPOSIT'
    restaurant_id?: string
    order_ids?: string[]
  }): Promise<{
    total_commands: number
    results: Array<{
      cabinet_id: string
      cabinet_code: string
      command_id: string | null
      job_id: string | null
      cell_ids: string[]
      cabinet_type: string
    }>
  }> {
    const payload = await httpClient.post<{
      total_commands?: number
      results?: Array<Record<string, unknown>>
    }>(`${this.base}workplace-cabinets/${workplaceId}/batch-open/`, params)
    return {
      total_commands: Number(payload?.total_commands ?? 0),
      results: Array.isArray(payload?.results) ? payload.results.map(r => ({
        cabinet_id: String(r.cabinet_id ?? ''),
        cabinet_code: String(r.cabinet_code ?? ''),
        command_id: r.command_id != null ? String(r.command_id) : null,
        job_id: r.job_id != null ? String(r.job_id) : null,
        cell_ids: Array.isArray(r.cell_ids) ? r.cell_ids.map(String) : [],
        cabinet_type: String(r.cabinet_type ?? 'SMART'),
      })) : [],
    }
  }

  // =====================================================================
  // P6: 打印机状态查询
  // =====================================================================

  /**
   * 查询单台打印机在线状态（实时调用芯烨云 API）
   * GET /api/v1/printers/{printer_sn}/status/
   *
   * @param printerSn - 打印机 SN 码
   * @returns PrinterStatusResult
   */
  async getPrinterStatus(printerSn: string): Promise<PrinterStatusResult> {
    try {
      const payload = await httpClient.get<PrinterStatusResult>(
        `${this.base}printers/${printerSn}/status/`
      )
      return payload
    } catch (e: unknown) {
      // 接口不可用时返回 UNKNOWN，不阻断业务
      console.warn('[HardwareApi] getPrinterStatus 失败，返回 UNKNOWN:', e)
      return {
        success: false,
        online_status: -1,
        status_label: '未知',
        user_message: '无法查询打印机状态（接口不可用），不影响正常打印',
        severity: 'info',
        printer_name: '',
        printer_sn: printerSn,
        checked_at: null,
      }
    }
  }

  /**
   * 获取所有异常打印机列表（用于出单中心告警横幅 / 定时轮询）
   * GET /api/v1/printers/status-alerts/
   *
   * 数据来源于 Celery Beat 每 5 分钟缓存的 PrintPrinter.online_status
   * @returns PrinterStatusAlertsResult
   */
  async getPrinterStatusAlerts(): Promise<PrinterStatusAlertsResult> {
    try {
      const payload = await httpClient.get<PrinterStatusAlertsResult>(
        `${this.base}printers/status-alerts/`
      )
      return payload
    } catch (e: unknown) {
      console.warn('[HardwareApi] getPrinterStatusAlerts 失败:', e)
      return {
        has_alerts: false,
        alerts: [],
        total_active: 0,
        total_abnormal: 0,
        total_online: 0,
        total_offline: 0,
        total_out_of_paper: 0,
        total_unknown: 0,
        total_stale: 0,
        freshness_seconds: 600,
        preview_limit: 20,
        has_more: false,
      }
    }
  }
}

// =====================================================================
// P6: 打印机状态相关类型
// =====================================================================

/** 单台打印机状态查询结果 */
export interface PrinterStatusResult {
  /** API 是否调用成功 */
  success: boolean
  /** 芯烨云状态码：0=离线, 1=在线, 2=缺纸, -1=未知 */
  online_status: number
  /** 状态标签（如"在线"、"离线"、"缺纸"） */
  status_label: string
  /** 给用户看的友好提示 */
  user_message: string
  /** 严重程度：success / warning / error / info */
  severity: 'success' | 'warning' | 'error' | 'info'
  /** 打印机名称 */
  printer_name: string
  /** 打印机 SN */
  printer_sn: string
  /** 状态检查时间 ISO-8601 */
  checked_at: string | null
}

/** 打印机状态告警列表中的单项 */
export interface PrinterStatusAlert {
  printer_name: string
  printer_sn: string
  /** 芯烨云状态码 */
  online_status: number
  status_label: string
  user_message: string
  severity: 'success' | 'warning' | 'error' | 'info'
  checked_at: string | null
  /** 状态是否超过新鲜度阈值 */
  stale: boolean
}

/** 批量打印机状态告警结果 */
export interface PrinterStatusAlertsResult {
  /** 是否有告警 */
  has_alerts: boolean
  /** 异常打印机列表 */
  alerts: PrinterStatusAlert[]
  /** 活跃打印机总数 */
  total_active: number
  /** 异常打印机数 */
  total_abnormal: number
  total_online: number
  total_offline: number
  total_out_of_paper: number
  total_unknown: number
  total_stale: number
  freshness_seconds: number
  preview_limit: number
  has_more: boolean
}

export const hardwareApi = new HardwareApiService()
